import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Check, X, Plus, Trash2, ArrowLeft, Users } from "lucide-react";

const ROLE_LABEL = {
  peer: "Peer",
  direct_report: "Direct Report",
  cross_functional: "Cross-Functional",
};
const ROLE_OPTIONS = [
  { value: "peer", label: "Peer" },
  { value: "direct_report", label: "Direct Report" },
  { value: "cross_functional", label: "Cross-Functional" },
];
const emptyAdd = () => ({ name: "", email: "", role_type: "peer", relationship_note: "" });

export default function Approve360() {
  const { cycleId } = useParams();
  const nav = useNavigate();
  const [cycle, setCycle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [decisions, setDecisions] = useState({});       // {nominee_id: "approved"|"rejected"}
  const [managerNotes, setManagerNotes] = useState({}); // {nominee_id: "note text"}
  const [extras, setExtras] = useState([]);              // manager-added raters
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/review-cycles/${cycleId}`)
      .then((r) => {
        setCycle(r.data);
        // Pre-fill decisions as "approved" so the manager only has to click
        // reject on the ones they don't want. Pre-fill notes from any
        // existing data (replay-safe).
        const d = {};
        const notes = {};
        for (const n of (r.data.nominated_raters || [])) {
          d[n.id] = n.status === "rejected" ? "rejected" : "approved";
          if (n.manager_note) notes[n.id] = n.manager_note;
        }
        setDecisions(d);
        setManagerNotes(notes);
      })
      .catch((e) => toast.error(apiError(e, "Could not load cycle")))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cycleId]);

  const toggle = (id) => setDecisions((d) => ({ ...d, [id]: d[id] === "rejected" ? "approved" : "rejected" }));
  const addExtra = () => setExtras((rows) => [...rows, emptyAdd()]);
  const removeExtra = (i) => setExtras((rows) => rows.filter((_, idx) => idx !== i));
  const updateExtra = (i, k, v) => setExtras((rows) => rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));

  const approvedCount = (() => {
    if (!cycle) return 0;
    const approvedFromProposed = (cycle.nominated_raters || []).filter((n) => decisions[n.id] !== "rejected").length;
    const validExtras = extras.filter((e) => e.email.trim() && e.name.trim()).length;
    return approvedFromProposed + validExtras;
  })();
  const peerCount = (() => {
    if (!cycle) return 0;
    const approvedPeers = (cycle.nominated_raters || []).filter((n) => decisions[n.id] !== "rejected" && n.role_type === "peer").length;
    const extraPeers = extras.filter((e) => e.email.trim() && e.name.trim() && e.role_type === "peer").length;
    return approvedPeers + extraPeers;
  })();

  const submit = async () => {
    if (!cycle) return;
    if (peerCount < cycle.min_peers) {
      toast.error(`At least ${cycle.min_peers} peers must be approved (currently ${peerCount}).`);
      return;
    }
    if (approvedCount > cycle.max_total_raters) {
      toast.error(`Max ${cycle.max_total_raters} raters allowed (currently ${approvedCount}).`);
      return;
    }
    setSaving(true);
    try {
      await api.post(`/review-cycles/${cycleId}/approve-nominations`, {
        decisions,
        manager_notes: managerNotes,
        added_nominees: extras
          .map((e) => ({ ...e, email: e.email.trim(), name: e.name.trim() }))
          .filter((e) => e.email && e.name),
      });
      toast.success("Approved — the cycle is now in collection.");
      nav(`/profiles/${cycle.profile_id}`);
    } catch (err) {
      toast.error(apiError(err, "Could not approve"));
      setSaving(false);
    }
  };

  if (loading) return <div className="max-w-3xl mx-auto p-12 font-mono-cal text-xs" data-testid="approve-loading">[LOADING…]</div>;
  if (!cycle) return null;
  const readOnly = cycle.status !== "awaiting_approval";

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="approve-page">
      <button onClick={() => nav(-1)} className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#666] hover:text-[#FF4F00] mb-4 inline-flex items-center gap-1" data-testid="approve-back">
        <ArrowLeft size={12} /> Back
      </button>
      <div className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#FF4F00] mb-2">[ 360° REVIEW · APPROVE RATERS ]</div>
      <h1 className="font-display font-bold text-4xl mb-1">{cycle.title}</h1>
      <p className="text-[#666] mb-8">
        {cycle.profile_snapshot?.employee_name} nominated <strong>{(cycle.nominated_raters || []).length}</strong> raters.
        Approve, reject, or add required raters below. Nobody is contacted until you submit.
      </p>

      {readOnly && (
        <div className="cal-card p-4 mb-6 border-[#FF4F00] bg-[#FFF5F0]" data-testid="approve-readonly-banner">
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00]">[ STATUS: {cycle.status.toUpperCase()} ]</div>
          <div className="text-sm text-[#333] mt-1">
            {cycle.status === "nominating"
              ? `${cycle.profile_snapshot?.employee_name} hasn't submitted nominations yet.`
              : `This cycle is past the approval stage. The approval decisions below are read-only.`}
          </div>
        </div>
      )}

      <div className="space-y-3 mb-6" data-testid="approve-nominees-list">
        {(cycle.nominated_raters || []).map((n) => {
          const isRejected = decisions[n.id] === "rejected";
          return (
            <div key={n.id} className={`cal-card p-4 flex flex-wrap gap-4 items-start ${isRejected ? "opacity-60 bg-[#FAFAFA]" : ""}`} data-testid={`approve-row-${n.id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="font-display font-bold text-lg">{n.name}</div>
                <div className="text-xs text-[#666] font-mono-cal">{n.email}</div>
                <div className="mt-1">
                  <span className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00]">[{ROLE_LABEL[n.role_type] || n.role_type}]</span>
                  {n.source === "employee" && <span className="ml-2 font-mono-cal text-[9px] text-[#666]">· nominated by employee</span>}
                </div>
                {n.relationship_note && (
                  <div className="mt-2 text-xs text-[#333] italic">"{n.relationship_note}"</div>
                )}
              </div>
              <div className="w-full md:w-auto flex items-center gap-2">
                <input
                  value={managerNotes[n.id] || ""}
                  onChange={(e) => setManagerNotes((m) => ({ ...m, [n.id]: e.target.value }))}
                  placeholder="Optional note…"
                  disabled={readOnly}
                  className="cal-input text-sm py-1.5 w-44"
                  data-testid={`approve-note-${n.id}`}
                />
                <button
                  type="button"
                  onClick={() => toggle(n.id)}
                  disabled={readOnly}
                  className={`cal-btn text-xs ${isRejected ? "cal-btn-ghost" : ""}`}
                  data-testid={`approve-toggle-${n.id}`}
                >
                  {isRejected ? <><X size={12} /> Rejected</> : <><Check size={12} /> Approved</>}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {!readOnly && (
        <>
          <div className="mb-4">
            <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-2">[ ADD ADDITIONAL RATERS (OPTIONAL) ]</div>
            {extras.map((e, i) => (
              <div key={i} className="cal-card p-3 mb-2 flex flex-wrap gap-2 items-center" data-testid={`extra-row-${i}`}>
                <input value={e.name} onChange={(ev) => updateExtra(i, "name", ev.target.value)} placeholder="Name" className="cal-input py-1.5 text-sm flex-1 min-w-[140px]" data-testid={`extra-name-${i}`}/>
                <input value={e.email} onChange={(ev) => updateExtra(i, "email", ev.target.value)} placeholder="email@company.com" type="email" className="cal-input py-1.5 text-sm flex-1 min-w-[180px]" data-testid={`extra-email-${i}`}/>
                <select value={e.role_type} onChange={(ev) => updateExtra(i, "role_type", ev.target.value)} className="cal-input py-1.5 text-sm" data-testid={`extra-role-${i}`}>
                  {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                <button type="button" onClick={() => removeExtra(i)} className="text-[#B00020] p-1" data-testid={`extra-remove-${i}`}><Trash2 size={14}/></button>
              </div>
            ))}
            <button type="button" onClick={addExtra} className="cal-btn cal-btn-ghost text-xs" data-testid="extra-add">
              <Plus size={12} /> Add rater
            </button>
          </div>

          <div className="cal-card p-4 flex items-center justify-between sticky bottom-4 bg-white" data-testid="approve-sticky-bar">
            <div className="text-sm">
              <strong className={peerCount >= cycle.min_peers ? "text-[#00A36C]" : "text-[#B00020]"}>{peerCount}</strong> peers · {approvedCount} total approved
              <span className="text-[#666] ml-2 text-xs">(min {cycle.min_peers} peers · max {cycle.max_total_raters} total)</span>
            </div>
            <button onClick={submit} disabled={saving} className="cal-btn" data-testid="approve-submit">
              {saving ? "Saving…" : <><Users size={14}/> Approve &amp; start collection →</>}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
